import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { switchChain } from '@wagmi/core';
import { Button, Select, Paragraph, AmountInput, ParagraphSize, ErrorBadge, ErrorLevel, Icon, IconNames, ButtonType, HelperButton, } from '@sovryn/ui';
import { useAccount as useSatsAccount } from '@gobob/sats-wagmi';
import { useWalletBalance } from 'hooks/useWalletBalance';
import { WalletType } from 'types/wallets';
import styles from '../../TransferPage.module.css';
import { DESTINATION_CHAINS, LOG_PEGIN_REQUESTS_API, MAX_BTC_AMOUNT, MIN_BTC_AMOUNT, SOURCE_CHAINS, } from '../../TransferPage.constants';
import { generateDestinationOptions } from '../../TransferPage.utils';
import { SimpleTable } from '@sovryn/ui';
import { SimpleTableRow } from '@sovryn/ui';
import { BTC_RENDER_PRECISION } from 'constants/general';
import { Token } from '../../TransferPage.types';
import { decimalic } from 'utils/math';
import { generateUserPeginRequest } from 'lib/peg-in';
import { BridgeState, tracker } from 'utils/tracker';
import classNames from 'classnames';
import { wagmiConfig } from 'configs/wagmi';
import { useAccount as useEvmAccount } from 'wagmi';
import { NetworkRenderer } from 'components/NetworkRenderer/NetworkRenderer';
import { StatusTracker } from 'components/StatusTracker/StatusTracker';
import { FaucetLink } from 'components/FaucetLink/FaucetLink';
import { shortenAddress } from 'utils/common';
import { useStore } from 'zustand';
import { toast } from 'react-toastify';
import { FaqSection } from 'components/FaqSection/FaqSection';
import { AmountRenderer } from 'components/AmountRenderer/AmountRenderer';
import { CaptchaVerification } from 'components/CaptchaVerification/CaptchaVerification';
import { nanoid } from 'nanoid';
const destinationOptions = generateDestinationOptions();
export const TransferForm = () => {
    const { address: btcWalletAddress, connector: btcConnector } = useSatsAccount();
    const { address: evmWalletAddress, connector: evmConnector, chainId, } = useEvmAccount();
    const { selectedChainId, actions } = useStore(tracker.store);
    const [showCaptcha, setShowCaptcha] = useState(false);
    const [showDepositCaptcha, setShowDepositCaptcha] = useState(false);
    const [btcDepositParams, setBtcDepositParams] = useState(null);
    const counter$ = useRef(0);
    const { balance, fullBalance } = useWalletBalance(btcWalletAddress ?? null, WalletType.BTC);
    const trackerAmount = decimalic(tracker.store.getState().context.evmRequest?.amount)
        .div(1e8)
        .toString();
    const [amount, setAmount] = useState(trackerAmount === '0' ? MIN_BTC_AMOUNT.toString() : trackerAmount);
    const [isValidAmount, setIsValidAmount] = useState(false);
    const token = Token.TBTC;
    useEffect(() => {
        if (!evmWalletAddress || amount === '') {
            setIsValidAmount(false);
            return;
        }
        const parsedAmount = parseFloat(amount);
        const isZeroAmount = parsedAmount === 0;
        setIsValidAmount(isZeroAmount ||
            (!isNaN(parsedAmount) &&
                parsedAmount >= MIN_BTC_AMOUNT &&
                parsedAmount <= MAX_BTC_AMOUNT &&
                (!balance || parsedAmount <= parseFloat(balance))));
    }, [amount, balance, evmWalletAddress]);
    const handleEvmRequest = useCallback(async () => {
        if (!evmConnector) {
            toast.error('Please connect your EVM wallet before transferring.');
            return;
        }
        const state = tracker.store.getState().context.evmRequest;
        if (!state) {
            toast.error('Unable to initiate the request. Please retry flow');
            tracker.setWarning('EVM Request Failed, try again...');
            return;
        }
        try {
            tracker.clearWarning();
            const hash = await generateUserPeginRequest(evmConnector, state.pegInId, BigInt(state.amount), evmWalletAddress);
            tracker.trackRequest(hash);
        }
        catch (error) {
            console.error('Failed to initiate the request:', error);
            tracker.setWarning('Failed to initiate the request, try again...');
        }
    }, [evmWalletAddress, evmConnector]);
    const handleTransfer = useCallback(async () => {
        counter$.current = 0;
        if (!isValidAmount) {
            toast.error('Please enter a valid BTC amount within the allowed range.');
            return;
        }
        if (!tracker.isCaptchaTokenValid()) {
            setShowCaptcha(true);
            return;
        }
        try {
            const satAmount = decimalic(amount)
                .mul(1e8)
                .div(1e18)
                .toBigNumber()
                .toBigInt();
            await tracker.initiateRequest(satAmount, evmWalletAddress);
            handleEvmRequest();
        }
        catch (error) {
            toast.error('Unable to initiate the transfer. Please try again.');
            console.warn('Transfer failed:', error);
        }
    }, [isValidAmount, amount, evmWalletAddress, handleEvmRequest]);
    const onCaptchaVerified = useCallback(() => {
        const token = tracker.getCaptchaToken();
        if (token) {
            setShowCaptcha(false);
            handleTransfer();
        }
        else {
            console.error('CAPTCHA verification failed. Please try again.');
        }
    }, [handleTransfer]);
    const onCaptchaError = useCallback(() => {
        setShowCaptcha(false);
    }, []);
    const handleSwitchNetwork = useCallback(async (value) => {
        try {
            if (!evmWalletAddress) {
                toast.error('Please connect your wallet before switching networks.');
                return;
            }
            actions.setSelectedChainId(value);
            await switchChain(wagmiConfig, { chainId: value });
            const networkLabel = DESTINATION_CHAINS.find(chain => chain.id === value)?.label;
            toast.success(`Switched to ${networkLabel || 'Unknown Network'}.`);
        }
        catch {
            toast.error('Unable to switch the network. Please try again.');
        }
    }, [evmWalletAddress, actions]);
    const isButtonDisabled = useMemo(() => {
        const parsedAmount = parseFloat(amount);
        return (!btcWalletAddress ||
            !evmWalletAddress ||
            !(parsedAmount !== 0 && parsedAmount >= MIN_BTC_AMOUNT) ||
            !isValidAmount);
    }, [btcWalletAddress, evmWalletAddress, amount, isValidAmount]);
    const handleLogPeginRequest = useCallback(async (params) => {
        const trackerContext = tracker.store.getState().context;
        const networkLabel = DESTINATION_CHAINS.find(chain => chain.id === selectedChainId)?.label;
        const captchaToken = tracker.getCaptchaToken();
        let idempotencyKey = tracker.getIdempotencyKey();
        if (!captchaToken) {
            setBtcDepositParams(params);
            setShowDepositCaptcha(true);
            return;
        }
        if (!idempotencyKey) {
            idempotencyKey = nanoid();
            tracker.setIdempotencyKey(idempotencyKey);
        }
        const bodyData = {
            peginId: params.state.pegInId,
            originChain: 'BTC',
            originAddress: btcWalletAddress,
            originWalletProvider: btcConnector?.name,
            originTxid: trackerContext.btcDeposit?.txId,
            targetChain: networkLabel,
            targetAddress: params.state.evmAddress,
            targetWalletProvider: evmConnector?.name,
            targetTxid: trackerContext.evmRequest?.hash,
            amount: params.state.amount,
            'cf-turnstile-response': captchaToken,
            idempotency_key: idempotencyKey,
        };
        fetch(LOG_PEGIN_REQUESTS_API, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(bodyData),
        })
            .then(response => response.json())
            .then(data => {
            console.log('Log pegin requests response:', data);
        })
            .catch(error => {
            console.error('Error during logging:', error);
        });
    }, [btcConnector, btcWalletAddress, evmConnector?.name, selectedChainId]);
    const onDepositCaptchaVerified = useCallback(() => {
        const token = tracker.getCaptchaToken();
        if (token && btcDepositParams) {
            setShowDepositCaptcha(false);
            handleLogPeginRequest(btcDepositParams);
            setBtcDepositParams(null);
        }
        else {
            console.error('CAPTCHA verification failed or deposit params are missing. Please try again.');
        }
    }, [handleLogPeginRequest, btcDepositParams]);
    const onDepositCaptchaError = useCallback(() => {
        setShowDepositCaptcha(false);
    }, []);
    const handleBtcDeposit = useCallback(async () => {
        counter$.current += 1;
        if (counter$.current > 1) {
            return;
        }
        const state = tracker.store.getState().context.evmResponse;
        if (!state || state.status === 'reverted') {
            toast.error('EVM request failed');
            tracker.setFailure('EVM request failed...');
            return;
        }
        if (!btcConnector) {
            toast.error('Please connect your BTC wallet before transferring.');
            return;
        }
        try {
            tracker.clearWarning();
            const btcHash = await btcConnector?.sendToAddress(state.taprootAddress, Number(state.amount));
            tracker.trackDeposit(btcHash, BigInt(state.amount));
            // Call logging function with state params
            await handleLogPeginRequest({ state });
        }
        catch (error) {
            console.error('Failed to send BTC:', error);
            tracker.setWarning('Failed to send BTC, try again...');
        }
    }, [btcConnector, handleLogPeginRequest]);
    const retryBtcDeposit = useCallback(() => {
        counter$.current = 0;
        return handleBtcDeposit();
    }, [handleBtcDeposit]);
    const handleUpdates = useCallback((state) => {
        console.log('state changed:', state);
        switch (state) {
            case BridgeState.evmRequestCompleted:
                // send btc
                handleBtcDeposit();
                break;
            case BridgeState.btcDepositCompleted:
                // track bridge tx (dummy)
                tracker.trackBridge();
                break;
        }
    }, [handleBtcDeposit]);
    useEffect(() => {
        console.log('intial state:', tracker.store.getState().context.state);
        handleUpdates(tracker.store.getState().context.state);
        const unsub = tracker.store.subscribe(state => state.context.state, handleUpdates);
        return () => unsub();
    }, [handleUpdates]);
    const isActiveWalletChainSupported = useMemo(() => DESTINATION_CHAINS.some(chain => chain.id === chainId), [chainId]);
    // Set the selected chain based on the user's network or select a default one
    useEffect(() => {
        if (chainId && !selectedChainId && btcWalletAddress) {
            if (isActiveWalletChainSupported) {
                actions.setSelectedChainId(chainId);
            }
            else {
                handleSwitchNetwork(destinationOptions[0].value);
            }
        }
    }, [
        actions,
        btcWalletAddress,
        chainId,
        handleSwitchNetwork,
        isActiveWalletChainSupported,
        selectedChainId,
    ]);
    // Change the selected chain if a user changes network in a wallet
    useEffect(() => {
        if (chainId &&
            selectedChainId &&
            chainId !== selectedChainId &&
            btcWalletAddress) {
            if (isActiveWalletChainSupported) {
                actions.setSelectedChainId(chainId);
            }
            else {
                handleSwitchNetwork(destinationOptions[0].value);
            }
        }
    }, [
        actions,
        btcWalletAddress,
        chainId,
        handleSwitchNetwork,
        isActiveWalletChainSupported,
        selectedChainId,
    ]);
    useEffect(() => {
        if (!btcWalletAddress) {
            actions.setSelectedChainId(destinationOptions[0].value);
        }
    }, [actions, btcWalletAddress]);
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex flex-col items-center", children: [_jsxs("div", { className: styles.form, children: [_jsxs("div", { className: "w-full flex flex-col justify-between items-center mb-3", children: [_jsxs("div", { className: "flex w-full justify-between items-center", children: [_jsx(Paragraph, { size: ParagraphSize.base, className: "font-medium text-gray-40 text-[0.8125rem] text-left w-full mb-2", children: "From" }), _jsx(FaucetLink, { className: "min-w-fit mb-2 font-medium text-[0.8125rem]" })] }), _jsx("div", { className: "flex items-center gap-2 w-full p-2.5 h-12 leading-7 bg-gray-80 rounded-[0.625rem] border border-gray-70 text-sm text-sov-white", children: _jsx(NetworkRenderer, { network: SOURCE_CHAINS[0] }) })] }), _jsx("div", { className: "flex justify-center rounded-full bg-gray-80 mx-auto h-4 w-4 items-center", children: _jsx(Icon, { icon: IconNames.TREND_ARROW_DOWN, className: "text-sov-white bg-[#242424]", size: 8 }) }), _jsxs("div", { className: "w-full flex flex-col justify-between items-center", children: [_jsx(Paragraph, { size: ParagraphSize.base, className: "font-medium text-gray-40 text-[0.8125rem] text-left w-full mb-2", children: "To" }), _jsx(Select, { disabled: !evmWalletAddress, options: destinationOptions, value: selectedChainId, onChange: handleSwitchNetwork, className: "justify-between gap-2 w-full p-2.5 h-12 leading-7 bg-gray-80 rounded-[0.625rem] border border-gray-70 text-sm text-sov-white disabled:bg-gray-80" })] }), _jsxs("div", { className: "w-full flex flex-col justify-between items-center", children: [_jsxs("div", { className: "flex items-center gap-1 mt-6 mb-2 w-full", children: [_jsx(Paragraph, { size: ParagraphSize.base, className: "font-medium text-gray-40 text-[0.8125rem]", children: "Recipient address" }), _jsx(HelperButton, { content: `This is the address where the ${token} will be sent.` })] }), _jsx("div", { className: "w-full p-2.5 h-12 leading-7 bg-gray-80 rounded-[0.625rem] border border-gray-70 text-sm text-gray-40 disabled:bg-gray-80", children: evmWalletAddress ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "block sm:hidden", children: shortenAddress(evmWalletAddress, 15, -15) }), _jsx("span", { className: "hidden sm:block", children: evmWalletAddress })] })) : ('Connect your wallet first') })] })] }), _jsx("div", { className: styles.form, children: _jsxs("div", { className: "w-full flex flex-col justify-between items-end gap-1", children: [_jsxs("div", { className: "flex text-xs text-gray-30 font-medium", children: [_jsx("div", { className: "mr-1", children: "Balance:" }), _jsx(AmountRenderer, { value: fullBalance || '0', precision: BTC_RENDER_PRECISION, suffix: token, useTooltip: false, className: "text-gray-30" })] }), _jsx(AmountInput, { value: amount, onChangeText: setAmount, label: _jsx("p", { className: "text-gray-30 text-sm font-medium m-0", children: "Amount" }), min: 0, invalid: !isValidAmount && evmWalletAddress, disabled: !balance || !evmWalletAddress, className: classNames(styles.amountInput, 'min-w-full'), placeholder: "0" }), isValidAmount === false && evmWalletAddress && amount && (_jsx(ErrorBadge, { level: ErrorLevel.Critical, message: "Amount must be within the allowed range.", className: classNames(styles.errorBadge, 'w-full border-0 bg-transparent justify-start mt-1 pt-0 pl-0') })), _jsxs("span", { className: "text-[0.8125rem] font-normal text-gray-40 text-left w-full", children: ["Recommended amount to send ", MIN_BTC_AMOUNT, " ", token] }), _jsxs(SimpleTable, { className: "w-full bg-transparent p-0 text-gray-40 mt-4", children: [_jsx(SimpleTableRow, { label: "You\u2019ll receive ", value: amount ? `${amount} ${token}` : `0 ${token}`, className: "text-[0.8125rem] font-normal text-gray-40" }), _jsx(SimpleTableRow, { label: "Estimated gas", value: `0.0000001 ${token}`, className: "text-[0.8125rem] font-normal text-gray-40" }), _jsx(SimpleTableRow, { label: "Total pay", value: amount
                                                ? `${decimalic(amount).add(0.0000001).toString()} ${token}`
                                                : `0 ${token}`, className: "text-gray-40 text-[0.8125rem] font-normal" })] })] }) }), _jsx("div", { className: "mb-6 max-w-72 mx-auto w-full", children: _jsx(Button, { type: ButtonType.reset, text: `Bridge ${token}`, style: [], className: "gradientTextButton mt-6 w-full rounded-[0.625rem] text-[#0B0A0A] border-0 font-inter font-semibold", onClick: handleTransfer, disabled: isButtonDisabled }) })] }), showCaptcha && (_jsx(CaptchaVerification, { btcAddress: btcWalletAddress, onVerified: onCaptchaVerified, onError: onCaptchaError })), showDepositCaptcha && (_jsx(CaptchaVerification, { btcAddress: btcWalletAddress, onVerified: onDepositCaptchaVerified, onError: onDepositCaptchaError })), _jsx(FaqSection, {}), _jsx(StatusTracker, { onHandleDeposit: retryBtcDeposit, onHandleRequest: handleEvmRequest })] }));
};
